import { useMemo } from 'react'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchIssues, fetchPRs, fetchWorkflowRuns, fetchCommits, fetchClosedPulls, fetchRecentlyClosedIssues, fetchRepos, addLabelsToIssue, removeLabelFromIssue, type Issue, type MergedPR, type ClosedUnmergedPR, type PR } from './github'
import { toRepo, type Repo } from './repos'
import { currentType, suggestType, type IssueType } from './issueType'
import { startOfDayInChile, startOfWeekInChile, startOfMonthInChile } from './oled'
import { redactRut } from './phi'

// Refresh interval: 120s. Con la lista dinámica (~13 repos) y tras deduplicar la
// llamada a /pulls?state=closed son 6 endpoints/repo = ~78 req/refresh →
// ~2340 req/h, bien bajo el límite de 5000 req/h (margen para 2 dashboards
// abiertos). En una TV 120s es imperceptible.
const REFRESH_MS = 120_000

// Lista dinámica de repos del board (reemplaza la antigua const REPOS). La lista
// cambia poco → refetch cada 5 min. Devuelve [] hasta que carga; todos los hooks
// que iteran repos lo consumen y useQueries maneja el array de largo variable.
export function useRepos(): Repo[] {
  const { data } = useQuery({
    queryKey: ['repos'],
    queryFn: fetchRepos,
    refetchInterval: 5 * 60_000,
    staleTime: 5 * 60_000,
  })
  return useMemo(() => (data ?? []).map(toRepo), [data])
}

// Decisión TV-pizarra: traer TODO el histórico relevante para que ningún
// thread desaparezca. Para PRs (merged + closed-no-merge) NO filtramos por
// fecha; GH ya devuelve los más recientes con sort=updated y per_page=100.
// Para issues cerrados sí pasamos `since=<1 año>` porque el endpoint lo
// requiere para acotar payload (los repos Wipxap no superan 100 closed/año).
const CLOSED_ISSUES_WINDOW_MS = 365 * 86_400_000

export function useIssues(repo: string) {
  return useQuery({
    queryKey: ['issues', repo],
    queryFn: () => fetchIssues(repo),
    refetchInterval: REFRESH_MS,
  })
}

export function useRecentlyClosedIssues(repo: string) {
  return useQuery({
    queryKey: ['issues-closed', repo],
    queryFn: () => {
      const sinceIso = new Date(Date.now() - CLOSED_ISSUES_WINDOW_MS).toISOString()
      return fetchRecentlyClosedIssues(repo, sinceIso)
    },
    refetchInterval: REFRESH_MS,
  })
}

// PRs cerrados sin merge — sin filtro temporal. La cell PRs los muestra como
// pills rojas (XCircle). GH ya devuelve los más recientes con sort=updated.
export function useClosedUnmergedPRs(repo: string) {
  return useQuery({
    queryKey: ['pulls-closed', repo],
    queryFn: () => fetchClosedPulls(repo),
    select: (d): ClosedUnmergedPR[] =>
      d.filter((p) => p.merged_at == null).map((p) => ({ ...p, merged_at: null })),
    refetchInterval: REFRESH_MS,
  })
}

export function usePRs(repo: string) {
  return useQuery({
    queryKey: ['prs', repo],
    queryFn: () => fetchPRs(repo),
    refetchInterval: REFRESH_MS,
  })
}

export function useRuns(repo: string) {
  return useQuery({
    queryKey: ['runs', repo],
    queryFn: () => fetchWorkflowRuns(repo),
    refetchInterval: REFRESH_MS,
  })
}

export function useCommits(repo: string) {
  // Devuelve la ventana completa de 30 días (para stats). El chip "N·24h" del
  // header filtra client-side en ProjectSection.
  return useQuery({
    queryKey: ['commits', repo],
    queryFn: () => fetchCommits(repo),
    refetchInterval: REFRESH_MS,
  })
}


export function useApplyType(repo: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ issueNumber, type }: { issueNumber: number; type: IssueType }) => {
      await addLabelsToIssue(repo, issueNumber, [type])
      return type
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['issues', repo] })
    },
  })
}

// Label canónico Wipxap: 'reported' marca un PR mergeado como ya comunicado al
// cliente (foto + texto via WhatsApp). Toggleable desde la TV.
export const REPORTED_LABEL = 'reported'

export function isReported(pr: MergedPR): boolean {
  return pr.labels.some((l) => l.name === REPORTED_LABEL)
}

export function useToggleReported(repo: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ prNumber, reported }: { prNumber: number; reported: boolean }) => {
      if (reported) {
        await removeLabelFromIssue(repo, prNumber, REPORTED_LABEL)
      } else {
        await addLabelsToIssue(repo, prNumber, [REPORTED_LABEL])
      }
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['pulls-closed', repo] })
    },
  })
}

// Actividad cross-repo agrupada por usuario, en TRES ventanas de CALENDARIO
// (zona Santiago, no rolling): día (desde 00:00 hoy), semana (desde el lunes) y
// mes (desde el día 1). Cada ventana es independiente: un evento cuenta en toda
// ventana cuyo inicio ya pasó (la semana puede cruzar el borde de mes). Mide 6
// categorías diferenciadas con iconos:
//   - issuesOpened / issuesClosed
//   - prsOpened / prsClosed (closed sin merge) / prsMerged
//   - commits a main
// Decisión de producto: "actividad" no "rendimiento" — la TV no tiene contexto
// para juzgar valor del trabajo, solo cuántas acciones se vieron.
//
// Los 3 miembros del equipo (Luis, Ismael, Jael) SIEMPRE aparecen, aunque tengan
// 0 actividad, para comparar de un vistazo. Cualquier otro login con actividad
// se agrega al final.
//
// Atribución: el endpoint /issues?state=closed list NO incluye `closed_by`.
// Atribuimos el cierre al autor original del issue (fallback). Si genera ruido
// real, migrar a /issues/{n} individual o GraphQL.
const TEAM: { login: string; name: string }[] = [
  { login: 'wipxap', name: 'Luis' },
  { login: 'IsmaelOrellanaWX', name: 'Ismael' },
  { login: 'JaelWX', name: 'Jael' },
]

export interface WindowStats {
  total: number
  issuesOpened: number
  issuesClosed: number
  prsOpened: number
  prsClosed: number
  prsMerged: number
  commits: number
}

export interface ContributorStats {
  login: string
  name: string
  day: WindowStats
  week: WindowStats
  month: WindowStats
}

export type CounterKey = keyof Omit<WindowStats, 'total'>

const emptyWindow = (): WindowStats => ({
  total: 0, issuesOpened: 0, issuesClosed: 0, prsOpened: 0, prsClosed: 0, prsMerged: 0, commits: 0,
})

export function useContributors(): ContributorStats[] {
  const repos = useRepos()
  const allOpenIssues = useQueries({
    queries: repos.map((r) => ({
      queryKey: ['issues', r.slug],
      queryFn: () => fetchIssues(r.slug),
      refetchInterval: REFRESH_MS,
    })),
  })
  const allClosedIssues = useQueries({
    queries: repos.map((r) => ({
      queryKey: ['issues-closed', r.slug],
      queryFn: () => {
        const sinceIso = new Date(Date.now() - CLOSED_ISSUES_WINDOW_MS).toISOString()
        return fetchRecentlyClosedIssues(r.slug, sinceIso)
      },
      refetchInterval: REFRESH_MS,
    })),
  })
  const allOpenPRs = useQueries({
    queries: repos.map((r) => ({
      queryKey: ['prs', r.slug],
      queryFn: () => fetchPRs(r.slug),
      refetchInterval: REFRESH_MS,
    })),
  })
  // Una sola query de /pulls?state=closed por repo (compartida con ProjectSection);
  // merged y closed-unmerged se derivan por merged_at en el loop de abajo.
  const allClosedPulls = useQueries({
    queries: repos.map((r) => ({
      queryKey: ['pulls-closed', r.slug],
      queryFn: () => fetchClosedPulls(r.slug),
      refetchInterval: REFRESH_MS,
    })),
  })
  const allCommits = useQueries({
    queries: repos.map((r) => ({
      queryKey: ['commits', r.slug],
      queryFn: () => fetchCommits(r.slug),
      refetchInterval: REFRESH_MS,
    })),
  })

  const dayCut = startOfDayInChile()
  const weekCut = startOfWeekInChile()
  const monthCut = startOfMonthInChile()
  // El evento cuenta en toda ventana cuyo inicio ya pasó (independientes: la
  // semana puede empezar antes que el mes si cruza el borde de mes).
  const earliest = Math.min(dayCut, weekCut, monthCut)

  const acc = new Map<string, ContributorStats>()
  const ensure = (login: string, name?: string) => {
    let s = acc.get(login)
    if (!s) {
      s = { login, name: name ?? login, day: emptyWindow(), week: emptyWindow(), month: emptyWindow() }
      acc.set(login, s)
    }
    return s
  }
  // Pre-seed del equipo: siempre visible aunque no tenga actividad.
  for (const t of TEAM) ensure(t.login, t.name)

  const bump = (login: string, key: CounterKey, iso: string | null | undefined) => {
    if (!iso) return
    const t = new Date(iso).getTime()
    if (t < earliest) return
    const s = ensure(login)
    if (t >= monthCut) { s.month[key]++; s.month.total++ }
    if (t >= weekCut) { s.week[key]++; s.week.total++ }
    if (t >= dayCut) { s.day[key]++; s.day.total++ }
  }

  for (const q of allOpenIssues)
    for (const i of q.data ?? []) if (i.user?.login) bump(i.user.login, 'issuesOpened', i.created_at)
  for (const q of allClosedIssues)
    for (const i of q.data ?? [])
      if (i.user?.login) bump(i.user.login, 'issuesClosed', (i as Issue & { closed_at?: string | null }).closed_at)
  for (const q of allOpenPRs)
    for (const p of q.data ?? []) if (p.user?.login) bump(p.user.login, 'prsOpened', p.created_at)
  for (const q of allClosedPulls)
    for (const p of q.data ?? []) {
      if (!p.user?.login) continue
      if (p.merged_at != null) bump(p.user.login, 'prsMerged', p.merged_at)
      else bump(p.user.login, 'prsClosed', p.closed_at)
    }
  for (const q of allCommits)
    for (const c of q.data ?? []) if (c.author?.login) bump(c.author.login, 'commits', c.commit.author.date)

  const teamOrder = new Map(TEAM.map((t, i) => [t.login, i]))
  return Array.from(acc.values()).sort((a, b) => {
    const ai = teamOrder.get(a.login) ?? 99
    const bi = teamOrder.get(b.login) ?? 99
    if (ai !== bi) return ai - bi
    return b.month.total - a.month.total
  })
}

// "Flow" del equipo (Csikszentmihalyi): narrativa cálida y colectiva del
// momentum, generada por Cloudflare Workers AI (functions/api/eval.ts). Arma un
// payload compacto con los totales por persona (día/semana/mes) — el LLM los usa
// internamente para narrar, NUNCA los expone como ranking. Si la IA no está
// (dev sin wrangler, o error) cae a un fallback determinista igual de motivador,
// así el panel nunca se rompe.
export interface FlowResult {
  state: string
  messages: string[]
  source: 'ai' | 'fallback'
}

type FocusItem = { sev: Severity; n: number; t: string; repo: string }
// Acto CONCRETO ocurrido HOY (merge, issue abierto/cerrado, PR abierto).
type DoneItem = { kind: string; n: number; t: string; repo: string; by: string }

const TEAM_NAME: Record<string, string> = Object.fromEntries(TEAM.map((t) => [t.login, t.name]))

// "Flow" del equipo alimentado con DATOS CONCRETOS (no genéricos): la IA recibe
// merges reales (#N + repo + quién), focos P0/P1 abiertos, PRs trancados y el
// pulso por persona → produce algo atingente y ÚTIL, no frases de galleta de la
// suerte. Los títulos van redactados con redactRut (jamás mandar RUT/PHI al
// modelo). Todas estas useQueries comparten queryKey con el board → 0 requests
// extra. Gate `ready` (loaded===total) para llamar 1 vez con la data completa.
export function useFlow(): FlowResult {
  const repos = useRepos()
  const contributors = useContributors()
  const { loaded, total } = useGlobalMetrics()

  const openIssuesQ = useQueries({
    queries: repos.map((r) => ({ queryKey: ['issues', r.slug], queryFn: () => fetchIssues(r.slug), refetchInterval: REFRESH_MS })),
  })
  const openPRsQ = useQueries({
    queries: repos.map((r) => ({ queryKey: ['prs', r.slug], queryFn: () => fetchPRs(r.slug), refetchInterval: REFRESH_MS })),
  })
  const closedPullsQ = useQueries({
    queries: repos.map((r) => ({ queryKey: ['pulls-closed', r.slug], queryFn: () => fetchClosedPulls(r.slug), refetchInterval: REFRESH_MS })),
  })
  const closedIssuesQ = useQueries({
    queries: repos.map((r) => ({
      queryKey: ['issues-closed', r.slug],
      queryFn: () => fetchRecentlyClosedIssues(r.slug, new Date(Date.now() - CLOSED_ISSUES_WINDOW_MS).toISOString()),
      refetchInterval: REFRESH_MS,
    })),
  })

  const nameOf = (slug: string) => repos.find((r) => r.slug === slug)?.name ?? slug.split('/')[1]
  const who = (login?: string | null) => (login ? (TEAM_NAME[login] ?? login) : 'alguien')
  const short = (s: string) => redactRut(s).slice(0, 80)
  const startOfDay = startOfDayInChile()
  const isToday = (iso?: string | null) => !!iso && new Date(iso).getTime() >= startOfDay

  // Lo hecho HOY (día calendario Santiago): merges + issues abiertos/cerrados +
  // PRs abiertos. Más reciente primero. NADA de histórico (semana/mes).
  const doneRaw: (DoneItem & { when: number })[] = []
  closedPullsQ.forEach((q, i) => {
    for (const p of q.data ?? [])
      if (p.merged_at && isToday(p.merged_at) && repos[i])
        doneRaw.push({ when: new Date(p.merged_at).getTime(), kind: 'mergeó', n: p.number, t: short(p.title), repo: nameOf(repos[i].slug), by: who(p.user?.login) })
  })
  openIssuesQ.forEach((q, i) => {
    for (const is of q.data ?? [])
      if (isToday(is.created_at) && repos[i])
        doneRaw.push({ when: new Date(is.created_at).getTime(), kind: 'abrió issue', n: is.number, t: short(is.title), repo: nameOf(repos[i].slug), by: who(is.user?.login) })
  })
  closedIssuesQ.forEach((q, i) => {
    for (const is of q.data ?? []) {
      const closedAt = (is as Issue & { closed_at?: string | null }).closed_at
      if (isToday(closedAt) && repos[i])
        doneRaw.push({ when: new Date(closedAt as string).getTime(), kind: 'cerró issue', n: is.number, t: short(is.title), repo: nameOf(repos[i].slug), by: who(is.user?.login) })
    }
  })
  openPRsQ.forEach((q, i) => {
    for (const pr of q.data ?? [])
      if (isToday(pr.created_at) && repos[i])
        doneRaw.push({ when: new Date(pr.created_at).getTime(), kind: 'abrió PR', n: pr.number, t: short(pr.title), repo: nameOf(repos[i].slug), by: who(pr.user?.login) })
  })
  const doneToday = doneRaw
    .sort((a, b) => b.when - a.when)
    .slice(0, 8)
    .map((d) => ({ kind: d.kind, n: d.n, t: d.t, repo: d.repo, by: d.by }))

  // Focos abiertos importantes a atacar HOY (P0 primero) — el backlog vivo, no histórico.
  const focusRaw: FocusItem[] = []
  openIssuesQ.forEach((q, i) => {
    for (const is of q.data ?? []) {
      const sev = getSeverity(is)
      if ((sev === 'p0' || sev === 'p1') && repos[i]) focusRaw.push({ sev, n: is.number, t: short(is.title), repo: nameOf(repos[i].slug) })
    }
  })
  const focus = focusRaw.sort((a, b) => (a.sev === 'p0' ? 0 : 1) - (b.sev === 'p0' ? 0 : 1)).slice(0, 6)

  // Pulso de HOY por persona.
  const pulse = contributors.map((c) => ({ name: c.name, hoy: c.day.total }))
  const dayTotal = pulse.reduce((s, p) => s + p.hoy, 0)

  const ready = total > 0 && loaded >= total
  const { data } = useQuery({
    queryKey: ['flow'],
    queryFn: async (): Promise<FlowResult> => {
      const res = await fetch('/api/eval', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pulse, doneToday, focus }),
      })
      if (!res.ok) throw new Error(`eval ${res.status}`)
      const j = (await res.json()) as { state?: string; messages?: unknown }
      const messages = Array.isArray(j.messages)
        ? j.messages.filter((m): m is string => typeof m === 'string' && m.trim().length > 0)
        : []
      if (messages.length === 0) throw new Error('sin messages')
      return { state: j.state ?? 'En flow', messages, source: 'ai' }
    },
    enabled: ready,
    refetchInterval: 30 * 60_000,
    staleTime: 30 * 60_000,
    retry: false,
  })

  return data ?? fallbackFlow({ dayTotal, doneToday, focus })
}

// Fallback SIN IA: mensajes anclados en lo de HOY (actos del día + foco a atacar
// hoy). Nunca humo — si hoy aún no hay actos, motiva apuntando al foco / arranque.
function fallbackFlow(d: { dayTotal: number; doneToday: DoneItem[]; focus: FocusItem[] }): FlowResult {
  const messages: string[] = []
  for (const it of d.doneToday.slice(0, 3)) messages.push(`Hoy ${it.by} ${it.kind} #${it.n} en ${it.repo}. 🚀`)
  const p0 = d.focus.find((f) => f.sev === 'p0') ?? d.focus[0]
  if (p0) messages.push(`Foco de hoy: ${p0.sev.toUpperCase()} #${p0.n} en ${p0.repo}. 🎯`)
  if (d.doneToday.length > 0) messages.push('Buen arranque hoy — a mantener el flow. 🔥')
  if (messages.length === 0) messages.push('Nuevo día, hoja en blanco. El primer commit prende el motor. 🚀')
  const state = d.dayTotal === 0 ? 'Arrancando' : d.dayTotal < 8 ? 'En marcha' : 'En racha'
  return { state, messages, source: 'fallback' }
}

// useMergedPRs y useClosedUnmergedPRs COMPARTEN la queryKey ['pulls-closed', repo]
// y la misma queryFn → React Query hace UNA sola request y cada hook deriva su
// slice con `select` (merged_at != null vs == null). Antes eran 2 requests a la
// misma URL por repo.
export function useMergedPRs(repo: string) {
  return useQuery({
    queryKey: ['pulls-closed', repo],
    queryFn: () => fetchClosedPulls(repo),
    select: (d): MergedPR[] => d.filter((p) => p.merged_at != null),
    refetchInterval: REFRESH_MS,
  })
}

// =============================================================================
// Derivaciones — severity y tipo de labels, agregados globales.
// =============================================================================

export type Severity = 'p0' | 'p1' | 'p2' | 'p3' | 'untriaged'

const SEV_LABELS: Record<Severity, RegExp> = {
  p0: /^p0\b|priority:\s*p0|sev:?\s*0/i,
  p1: /^p1\b|priority:\s*p1|sev:?\s*1/i,
  p2: /^p2\b|priority:\s*p2|sev:?\s*2/i,
  p3: /^p3\b|priority:\s*p3|sev:?\s*3/i,
  untriaged: /never-matches-fallback/,
}

export function getSeverity(issue: Issue): Severity {
  for (const sev of ['p0', 'p1', 'p2', 'p3'] as const) {
    if (issue.labels.some((l) => SEV_LABELS[sev].test(l.name))) return sev
  }
  return 'untriaged'
}

// Tipo efectivo del issue: el label aplicado si lo hay, sino sugerencia por
// heurística. Define la categorización canónica Wipxap (issueType.ts).
function effectiveType(issue: Issue) {
  return currentType(issue) ?? suggestType(issue)
}

export function ageInDays(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000)
}

// Tiempo relativo compacto para TV (1-3 chars típicamente).
//   <1m → "now"   1-59m → "5m"   1-23h → "3h"   1-29d → "5d"
//   30d-11mo → "2mo"   >=12mo → "1y"
export function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime()
  if (diffMs < 0) return 'now'
  const minutes = Math.floor(diffMs / 60_000)
  if (minutes < 1) return 'now'
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months}mo`
  return `${Math.floor(months / 12)}y`
}

// Timestamp absoluto local CL para tooltip ("27/05/2026 13:45").
export function localTimestamp(iso: string): string {
  return new Date(iso).toLocaleString('es-CL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

export interface GlobalMetrics {
  totalP0: number
  prsOver7Days: number
  bugsThisWeek: number
  featsThisWeek: number
  ciGreenCount: number
  // Repos con CI configurado (workflow_runs > 0). Denominador real para "X verdes de Y".
  ciConfiguredCount: number
  loaded: number
  total: number
}

export function useGlobalMetrics(): GlobalMetrics {
  const repos = useRepos()
  const allIssues = useQueries({
    queries: repos.map((r) => ({
      queryKey: ['issues', r.slug],
      queryFn: () => fetchIssues(r.slug),
      refetchInterval: REFRESH_MS,
    })),
  })

  const allPRs = useQueries({
    queries: repos.map((r) => ({
      queryKey: ['prs', r.slug],
      queryFn: () => fetchPRs(r.slug),
      refetchInterval: REFRESH_MS,
    })),
  })

  const allRuns = useQueries({
    queries: repos.map((r) => ({
      queryKey: ['runs', r.slug],
      queryFn: () => fetchWorkflowRuns(r.slug),
      refetchInterval: REFRESH_MS,
    })),
  })

  const weekAgo = Date.now() - 7 * 86_400_000

  const issues = allIssues.flatMap((q) => q.data ?? [])
  const prs = allPRs.flatMap((q) => q.data ?? [])

  const totalP0 = issues.filter((i) => getSeverity(i) === 'p0').length
  const prsOver7Days = prs.filter((p: PR) => new Date(p.created_at).getTime() < weekAgo).length

  const bugsThisWeek = issues.filter(
    (i) => effectiveType(i) === 'bug' && new Date(i.created_at).getTime() > weekAgo,
  ).length
  const featsThisWeek = issues.filter(
    (i) => effectiveType(i) === 'feature' && new Date(i.created_at).getTime() > weekAgo,
  ).length

  const ciConfiguredCount = allRuns.filter((q) => (q.data?.length ?? 0) > 0).length
  const ciGreenCount = allRuns.filter((q) => {
    const last = q.data?.[0]
    return last && last.conclusion === 'success'
  }).length

  const loaded = allIssues.filter((q) => q.isSuccess).length
  return {
    totalP0,
    prsOver7Days,
    bugsThisWeek,
    featsThisWeek,
    ciGreenCount,
    ciConfiguredCount,
    loaded,
    total: repos.length,
  }
}
